import { toast } from 'sonner'

import { PaymentRails } from '@/domains/Business/constants'

import {
  CreatedRecipient,
  EntityType,
  SharedRecipientFormProps,
} from '../types'

import { EURecipientForm } from './EURecipientForm'
import { MXRecipientForm } from './MXRecipientForm'
import { USRecipientForm } from './USRecipientForm'

type Props = {
  recipient?: CreatedRecipient
  onContinue: (recipient: CreatedRecipient) => void
  paymentRails?: PaymentRails
} & SharedRecipientFormProps

export const RecipientForm = ({
  layout = 'create',
  paymentRails,
  isPending,
  recipient,
  recipientId,
  onContinue,
  onDelete,
}: Props) => {
  switch (paymentRails) {
    case PaymentRails.MEX: {
      return (
        <MXRecipientForm
          isPending={isPending}
          layout={layout}
          recipient={recipient}
          onDelete={onDelete}
          recipientId={recipientId}
          onContinue={(values) => {
            switch (values.entityType) {
              case EntityType.INDIVIDUAL: {
                if (!values.firstName || !values.lastName) {
                  toast.error('first and last name are required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.INDIVIDUAL,
                    details: {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      secondLastName: values.secondLastName,
                    },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.MEX,
                    railsDetails: { clabe: values.clabe },
                  },
                })
              }

              case EntityType.BUSINESS: {
                if (!values.legalName) {
                  toast.error('legal name is required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.BUSINESS,
                    details: {
                      legalName: values.legalName,
                    },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.MEX,
                    railsDetails: { clabe: values.clabe },
                  },
                })
              }

              default:
                return null
            }
          }}
        />
      )
    }

    case PaymentRails.USA: {
      return (
        <USRecipientForm
          isPending={isPending}
          layout={layout}
          onDelete={onDelete}
          recipient={recipient}
          recipientId={recipientId}
          onContinue={(values) => {
            switch (values.entityType) {
              case EntityType.INDIVIDUAL: {
                if (!values.firstName || !values.lastName) {
                  toast.error('first and last name are required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.INDIVIDUAL,
                    details: {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      secondLastName: values.secondLastName,
                    },
                    address: { ...values.address },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.USA,
                    railsDetails: {
                      accountNumber: values.accountNumber,
                      routingNumber: values.routingNumber,
                      paymentMethod: values.paymentMethod,
                    },
                  },
                })
              }

              case EntityType.BUSINESS: {
                if (!values.legalName) {
                  toast.error('legal name is required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.BUSINESS,
                    details: {
                      legalName: values.legalName,
                    },
                    address: { ...values.address },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.USA,
                    railsDetails: {
                      accountNumber: values.accountNumber,
                      routingNumber: values.routingNumber,
                      paymentMethod: values.paymentMethod,
                    },
                  },
                })
              }

              default:
                return null
            }
          }}
        />
      )
    }

    case PaymentRails.EU: {
      return (
        <EURecipientForm
          isPending={isPending}
          layout={layout}
          onDelete={onDelete}
          recipient={recipient}
          recipientId={recipientId}
          onContinue={(values) => {
            switch (values.entityType) {
              case EntityType.INDIVIDUAL: {
                if (!values.firstName || !values.lastName) {
                  toast.error('first and last name are required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.INDIVIDUAL,
                    details: {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      secondLastName: values.secondLastName,
                    },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.EU,
                    railsDetails: { iban: values.iban },
                  },
                })
              }

              case EntityType.BUSINESS: {
                if (!values.legalName) {
                  toast.error('legal name is required')
                  return
                }

                return onContinue({
                  nickname: values.nickname,
                  recipient: {
                    type: EntityType.BUSINESS,
                    details: {
                      legalName: values.legalName,
                    },
                  },
                  contactDetails: {
                    internationalPhonePrefix: values.internationalPhonePrefix,
                    localPhoneNumber: values.localPhoneNumber,
                    email: values.email,
                  },
                  paymentInformation: {
                    rails: PaymentRails.EU,
                    railsDetails: { iban: values.iban },
                  },
                })
              }

              default:
                return null
            }
          }}
        />
      )
    }

    default:
      return null
  }
}
